import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

gsap?.registerPlugin(ScrollTrigger);

const TestimonialsSection = () => {
  const [language, setLanguage] = useState('en');
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const sectionRef = useRef(null);
  const carouselRef = useRef(null);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setLanguage(savedLanguage);
  }, []);

  useEffect(() => {
    const ctx = gsap?.context(() => {
      gsap?.fromTo(
        carouselRef?.current,
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          scrollTrigger: {
            trigger: carouselRef?.current,
            start: 'top 70%',
            toggleActions: 'play none none reverse',
          },
        }
      );
    }, sectionRef);

    return () => ctx?.revert();
  }, []);

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setActiveTestimonial((prev) =>
        prev === content?.[language]?.testimonials?.length - 1 ? 0 : prev + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, language]);

  const content = {
    en: {
      title: 'What Our Customers Say',
      subtitle: 'Real businesses sharing their success stories with Slotyfi',
      testimonials: [
        {
          id: 1,
          name: 'Sarah Johnson',
          title: 'Owner, Bella Beauty Salon',
          company: 'Bella Beauty Salon',
          location: 'New York, USA',
          avatar:
            'https://img.rocket.new/generatedImages/rocket_gen_img_190332ac2-1762273805567.png',
          avatarAlt:
            'Professional headshot of Sarah Johnson, a blonde woman in her 30s wearing a navy blazer, smiling warmly at camera',
          rating: 5,
          quote:
            "Slotyfi reduced our no-shows by 90% and saved us 15 hours per week. Our clients love the convenience of booking through WhatsApp and Instagram. It's been a game-changer for our business.",
          metrics: {
            noShowReduction: '90%',
            timeSaved: '15 hours/week',
            revenueIncrease: '35%',
          },
          businessType: 'Beauty Salon',
          staffSize: '8 staff members',
        },
        {
          id: 2,
          name: 'Dr. Michael Chen',
          title: 'Clinic Director',
          company: 'Downtown Medical Center',
          location: 'San Francisco, USA',
          avatar:
            'https://img.rocket.new/generatedImages/rocket_gen_img_1456d520e-1762274360201.png',
          avatarAlt:
            'Professional headshot of Dr. Michael Chen, an Asian man in his 40s wearing a white medical coat and stethoscope',
          rating: 5,
          quote:
            'The AI booking assistant handles complex medical scheduling perfectly. Patients can book follow-ups, reschedule appointments, and receive automated reminders. Our efficiency has improved dramatically.',
          metrics: {
            noShowReduction: '85%',
            timeSaved: '25 hours/week',
            revenueIncrease: '42%',
          },
          businessType: 'Medical Clinic',
          staffSize: '15 staff members',
        },
        {
          id: 3,
          name: 'Emma Rodriguez',
          title: 'Spa Manager',
          company: 'Zen Wellness Spa',
          location: 'Miami, USA',
          avatar:
            'https://img.rocket.new/generatedImages/rocket_gen_img_190332ac2-1762273805567.png',
          avatarAlt:
            'Professional headshot of Emma Rodriguez, a Hispanic woman in her 30s with long dark hair wearing a white spa uniform',
          rating: 5,
          quote:
            "Our multi-location spa needed unified booking management. Slotyfi's dashboard gives us real-time visibility across all locations. The Instagram integration alone brought us 200+ new clients.",
          metrics: {
            noShowReduction: '88%',
            timeSaved: '30 hours/week',
            revenueIncrease: '55%',
          },
          businessType: 'Wellness Spa',
          staffSize: '22 staff members',
        },
        {
          id: 4,
          name: 'James Wilson',
          title: 'Fitness Studio Owner',
          company: 'FitCore Training',
          location: 'Austin, USA',
          avatar:
            'https://img.rocket.new/generatedImages/rocket_gen_img_187debda7-1762273710176.png',
          avatarAlt:
            'Professional headshot of James Wilson, a fit Caucasian man in his 30s wearing a black athletic shirt, smiling confidently',
          rating: 5,
          quote:
            "Group class scheduling was our biggest headache. Now members book classes through Telegram, get automatic waitlist notifications, and we've eliminated double bookings completely.",
          metrics: {
            noShowReduction: '92%',
            timeSaved: '20 hours/week',
            revenueIncrease: '38%',
          },
          businessType: 'Fitness Studio',
          staffSize: '6 staff members',
        },
      ],

      navigation: {
        previous: 'Previous',
        next: 'Next',
        pause: 'Pause',
        play: 'Play',
      },
      cta: {
        title: 'Ready to Join Them?',
        subtitle: 'Start your success story today',
        button: 'Book Your Demo',
      },
    },
    fa: {
      title: 'آنچه مشتریان ما می‌گویند',
      subtitle:
        'کسب‌وکارهای واقعی داستان‌های موفقیت خود را با اسلاتیفای به اشتراک می‌گذارند',
      testimonials: [
        {
          id: 1,
          name: 'سارا جانسون',
          title: 'مالک، سالن زیبایی بلا',
          company: 'سالن زیبایی بلا',
          location: 'نیویورک، آمریکا',
          avatar:
            'https://images.unsplash.com/photo-1654727169791-7f46d0dfc1a3',
          avatarAlt:
            'عکس پروفایل حرفه‌ای سارا جانسون، زنی بلوند در سی‌سالگی با کت تیره‌ای که گرمانه لبخند می‌زند',
          rating: 5,
          quote:
            'اسلاتیفای غیبت‌های ما را 90% کاهش داد و 15 ساعت در هفته برای ما صرفه‌جویی کرد. مشتریان ما از راحتی رزرو از طریق واتساپ و اینستاگرام لذت می‌برند. این برای کسب‌وکار ما تغییر بازی بوده است.',
          metrics: {
            noShowReduction: '90%',
            timeSaved: '15 ساعت/هفته',
            revenueIncrease: '35%',
          },
          businessType: 'سالن زیبایی',
          staffSize: '8 عضو کارکنان',
        },
        {
          id: 2,
          name: 'دکتر مایکل چن',
          title: 'مدیر کلینیک',
          company: 'مرکز پزشکی مرکز شهر',
          location: 'سان فرانسیسکو، آمریکا',
          avatar:
            'https://images.unsplash.com/photo-1582750433449-648ed127bb54',
          avatarAlt:
            'عکس پروفایل حرفه‌ای دکتر مایکل چن، مردی آسیایی در چهل‌سالگی با روپوش سفید پزشکی و گوشی پزشکی',
          rating: 5,
          quote:
            'دستیار رزرو هوش مصنوعی برنامه‌ریزی پیچیده پزشکی را کاملاً مدیریت می‌کند. بیماران می‌توانند پیگیری‌ها را رزرو کنند، قرارملاقات‌ها را تغییر دهند و یادآوری‌های خودکار دریافت کنند. کارایی ما به طور چشمگیری بهبود یافته است.',
          metrics: {
            noShowReduction: '85%',
            timeSaved: '25 ساعت/هفته',
            revenueIncrease: '42%',
          },
          businessType: 'کلینیک پزشکی',
          staffSize: '15 عضو کارکنان',
        },
        {
          id: 3,
          name: 'اما رودریگز',
          title: 'مدیر اسپا',
          company: 'اسپای سلامت زن',
          location: 'میامی، آمریکا',
          avatar:
            'https://img.rocket.new/generatedImages/rocket_gen_img_190332ac2-1762273805567.png',
          avatarAlt:
            'عکس پروفایل حرفه‌ای اما رودریگز، زنی هیسپانیک در سی‌سالگی با موهای بلند تیره و لباس سفید اسپا',
          rating: 5,
          quote:
            'اسپای چند مکانی ما به مدیریت رزرو یکپارچه نیاز داشت. داشبورد اسلاتیفای دید زمان واقعی در همه مکان‌ها به ما می‌دهد. یکپارچگی اینستاگرام به تنهایی 200+ مشتری جدید برای ما آورد.',
          metrics: {
            noShowReduction: '88%',
            timeSaved: '30 ساعت/هفته',
            revenueIncrease: '55%',
          },
          businessType: 'اسپای سلامت',
          staffSize: '22 عضو کارکنان',
        },
        {
          id: 4,
          name: 'جیمز ویلسون',
          title: 'مالک استودیو تناسب اندام',
          company: 'تمرین فیت کور',
          location: 'آستین، آمریکا',
          avatar:
            'https://images.unsplash.com/photo-1735317146081-f8671e25855c',
          avatarAlt:
            'عکس پروفایل حرفه‌ای جیمز ویلسون، مردی قفقازی تناسب اندام در سی‌سالگی با تی‌شرت ورزشی سیاه که با اعتماد لبخند می‌زند',
          rating: 5,
          quote:
            'برنامه‌ریزی کلاس گروهی بزرگترین دردسر ما بود. حالا اعضا کلاس‌ها را از طریق تلگرام رزرو می‌کنند، اعلان‌های خودکار لیست انتظار دریافت می‌کنند و ما رزروهای مضاعف را کاملاً حذف کرده‌ایم.',
          metrics: {
            noShowReduction: '92%',
            timeSaved: '20 ساعت/هفته',
            revenueIncrease: '38%',
          },
          businessType: 'استودیو تناسب اندام',
          staffSize: '6 عضو کارکنان',
        },
      ],

      navigation: {
        previous: 'قبلی',
        next: 'بعدی',
        pause: 'توقف',
        play: 'پخش',
      },
      cta: {
        title: 'آماده پیوستن به آن‌ها هستید؟',
        subtitle: 'داستان موفقیت خود را امروز شروع کنید',
        button: 'دمو خود را رزرو کنید',
      },
    },
  };

  const currentContent = content?.[language];
  const activeTestimonialData =
    currentContent?.testimonials?.[activeTestimonial];

  const nextTestimonial = () => {
    setActiveTestimonial((prev) =>
      prev === currentContent?.testimonials?.length - 1 ? 0 : prev + 1
    );
  };

  const prevTestimonial = () => {
    setActiveTestimonial((prev) =>
      prev === 0 ? currentContent?.testimonials?.length - 1 : prev - 1
    );
  };

  const toggleAutoPlay = () => {
    setIsAutoPlaying(!isAutoPlaying);
  };

  return (
    <section ref={sectionRef} id="testimonials" className="py-20 bg-white">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div
          className={`text-center mb-16 ${language === 'fa' ? 'rtl' : 'ltr'}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {currentContent?.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {currentContent?.subtitle}
          </p>
        </div>

        {/* Testimonial Carousel */}
        <div ref={carouselRef} className="max-w-6xl mx-auto">
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-3xl p-8 md:p-12 shadow-xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Testimonial Content */}
              <div
                className={`space-y-6 ${
                  language === 'fa' ? 'text-right' : 'text-left'
                }`}>
                {/* Quote */}
                <div className="relative">
                  <Icon
                    name="Quote"
                    size={48}
                    color="#3B82F6"
                    className={`absolute ${
                      language === 'fa' ? '-right-4 -top-4' : '-left-4 -top-4'
                    } opacity-20`}
                  />

                  <blockquote className="text-xl md:text-2xl text-gray-800 leading-relaxed font-medium relative z-10">
                    "{activeTestimonialData?.quote}"
                  </blockquote>
                </div>

                {/* Rating */}
                <div className="flex items-center space-x-1">
                  {[...Array(5)]?.map((_, i) => (
                    <Icon
                      key={i}
                      name="Star"
                      size={20}
                      color={
                        i < activeTestimonialData?.rating
                          ? '#F59E0B'
                          : '#E5E7EB'
                      }
                    />
                  ))}
                </div>

                {/* Author Info */}
                <div className="flex items-center space-x-4">
                  <Image
                    src={activeTestimonialData?.avatar}
                    alt={activeTestimonialData?.avatarAlt}
                    className="w-16 h-16 rounded-full object-cover"
                  />

                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">
                      {activeTestimonialData?.name}
                    </h4>
                    <p className="text-gray-600">
                      {activeTestimonialData?.title}
                    </p>
                    <p className="text-sm text-gray-500">
                      {activeTestimonialData?.location}
                    </p>
                  </div>
                </div>

                {/* Business Info */}
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Business Type:</span>
                      <div className="font-medium text-gray-900">
                        {activeTestimonialData?.businessType}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-500">Team Size:</span>
                      <div className="font-medium text-gray-900">
                        {activeTestimonialData?.staffSize}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Metrics */}
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-gray-900 text-center">
                  Results Achieved
                </h3>

                <div className="grid gap-4">
                  <div className="bg-white rounded-xl p-6 text-center shadow-sm">
                    <div className="text-3xl font-bold text-green-600 mb-2">
                      {activeTestimonialData?.metrics?.noShowReduction}
                    </div>
                    <div className="text-gray-600">No-Show Reduction</div>
                  </div>

                  <div className="bg-white rounded-xl p-6 text-center shadow-sm">
                    <div className="text-3xl font-bold text-blue-600 mb-2">
                      {activeTestimonialData?.metrics?.timeSaved}
                    </div>
                    <div className="text-gray-600">Time Saved</div>
                  </div>

                  <div className="bg-white rounded-xl p-6 text-center shadow-sm">
                    <div className="text-3xl font-bold text-purple-600 mb-2">
                      {activeTestimonialData?.metrics?.revenueIncrease}
                    </div>
                    <div className="text-gray-600">Revenue Increase</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Controls */}
            <div className="flex items-center justify-between mt-8 pt-8 border-t border-gray-200">
              <div className="flex items-center space-x-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={prevTestimonial}
                  iconName="ChevronLeft"
                  className="text-gray-600 hover:text-blue-600">
                  {currentContent?.navigation?.previous}
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleAutoPlay}
                  iconName={isAutoPlaying ? 'Pause' : 'Play'}
                  className="text-gray-600 hover:text-blue-600">
                  {isAutoPlaying
                    ? currentContent?.navigation?.pause
                    : currentContent?.navigation?.play}
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={nextTestimonial}
                  iconName="ChevronRight"
                  className="text-gray-600 hover:text-blue-600">
                  {currentContent?.navigation?.next}
                </Button>
              </div>

              {/* Dots Indicator */}
              <div className="flex space-x-2">
                {currentContent?.testimonials?.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveTestimonial(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === activeTestimonial
                        ? 'bg-blue-600 scale-125'
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            {currentContent?.cta?.title}
          </h3>
          <p className="text-gray-600 mb-8">{currentContent?.cta?.subtitle}</p>
          <Button
            variant="default"
            size="lg"
            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4"
            iconName="Calendar"
            iconPosition="left">
            {currentContent?.cta?.button}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
